import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Plus, Check, Trash2, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountSwitcher = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = () => {
    const savedAccounts = JSON.parse(localStorage.getItem('chatbuzzz_accounts') || '[]');
    setAccounts(savedAccounts);
  };

  const saveAccounts = (newAccounts) => {
    localStorage.setItem('chatbuzzz_accounts', JSON.stringify(newAccounts));
    setAccounts(newAccounts);
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: username.trim(),
        password: password.trim()
      });

      const { access_token, user: newUser } = response.data;
      
      // Check if account already exists
      const existingIndex = accounts.findIndex(a => a.user.id === newUser.id);
      if (existingIndex >= 0) {
        // Update existing account token
        const newAccounts = [...accounts];
        newAccounts[existingIndex] = {
          user: newUser,
          token: access_token
        };
        saveAccounts(newAccounts);
        toast.success('Account updated');
      } else {
        // Add new account
        const newAccounts = [...accounts, {
          user: newUser,
          token: access_token
        }];
        saveAccounts(newAccounts);
        toast.success('Account added');
      }

      setShowAddAccount(false);
      setUsername('');
      setPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const switchToAccount = (account) => {
    if (account.user.id === user?.id) {
      toast.info('Already using this account');
      return;
    }

    // Save current account if not already saved
    const currentAccountExists = accounts.some(a => a.user.id === user?.id);
    if (!currentAccountExists && user && token) {
      const newAccounts = [...accounts, { user, token }];
      saveAccounts(newAccounts);
    }

    // Switch to new account
    localStorage.setItem('token', account.token);
    window.location.href = '/chats';
  };

  const removeAccount = (accountId) => {
    if (accountId === user?.id) {
      toast.error('Cannot remove current account');
      return;
    }

    const newAccounts = accounts.filter(a => a.user.id !== accountId);
    saveAccounts(newAccounts);
    toast.success('Account removed');
  };

  // Add current account to list if not present (only once on mount)
  useEffect(() => {
    if (user && token) {
      setAccounts(prevAccounts => {
        const currentAccountExists = prevAccounts.some(a => a.user.id === user.id);
        if (!currentAccountExists) {
          const newAccounts = [...prevAccounts, { user, token }];
          localStorage.setItem('chatbuzzz_accounts', JSON.stringify(newAccounts));
          return newAccounts;
        }
        return prevAccounts;
      });
    }
  }, []); // Empty dependency array - only run once on mount

  return (
    <div className="min-h-screen bg-background-DEFAULT flex flex-col" data-testid="account-switcher-page">
      <div className="p-6 border-b border-border-DEFAULT">
        <div className="chat-container flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-btn"
              onClick={() => navigate('/settings')}
              className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary-DEFAULT transition-colors"
            >
              <ArrowLeft size={20} className="text-text-primary" />
            </button>
            <h1 className="font-heading text-2xl text-text-primary">Accounts</h1>
          </div>
          <button
            data-testid="add-account-btn"
            onClick={() => setShowAddAccount(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors"
          >
            <Plus size={18} />
            Add Account
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide p-6">
        <div className="chat-container max-w-2xl mx-auto space-y-4">
          {/* Current Account */}
          {user && (
            <div className="bg-white rounded-3xl p-6 shadow-card border-2 border-primary">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium text-xl">
                  {user.display_name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-text-primary">{user.display_name}</h3>
                    <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">Active</span>
                  </div>
                  <p className="text-sm text-text-secondary">@{user.username}</p>
                </div>
                <Check size={24} className="text-primary" />
              </div>
            </div>
          )}

          {/* Other Accounts */}
          {accounts.filter(a => a.user.id !== user?.id).map((account) => (
            <div 
              key={account.user.id}
              className="bg-white rounded-3xl p-6 shadow-card border border-border-DEFAULT hover:border-primary transition-colors"
            >
              <div className="flex items-center gap-4">
                <button
                  data-testid={`switch-to-${account.user.username}`}
                  onClick={() => switchToAccount(account)}
                  className="flex items-center gap-4 flex-1"
                >
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center text-white font-medium text-xl">
                    {account.user.display_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-medium text-text-primary">{account.user.display_name}</h3>
                    <p className="text-sm text-text-secondary">@{account.user.username}</p>
                  </div>
                </button>
                <button
                  data-testid={`remove-${account.user.username}`}
                  onClick={() => removeAccount(account.user.id)}
                  className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors"
                >
                  <Trash2 size={18} className="text-red-500" />
                </button>
              </div>
            </div>
          ))}

          {accounts.length <= 1 && (
            <div className="text-center py-8 text-text-secondary">
              <User size={48} className="mx-auto mb-4 text-text-tertiary" />
              <p>No other accounts added yet.</p>
              <p className="text-sm">Tap "Add Account" to link another account.</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Account Modal */}
      {showAddAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md" data-testid="add-account-modal">
            <h2 className="font-heading text-xl text-text-primary mb-6">Add Account</h2>
            <form onSubmit={handleAddAccount} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Username
                </label>
                <input
                  data-testid="add-username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  className="w-full px-4 py-3 border border-border-DEFAULT rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Password
                </label>
                <input
                  data-testid="add-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full px-4 py-3 border border-border-DEFAULT rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  data-testid="cancel-add-btn"
                  onClick={() => {
                    setShowAddAccount(false);
                    setUsername('');
                    setPassword('');
                  }}
                  className="flex-1 py-3 border border-border-DEFAULT rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="confirm-add-btn"
                  disabled={loading}
                  className="flex-1 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Adding...' : 'Add Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSwitcher;
